import { apiGet, apiPost, apiPatch, apiDelete } from "./client";
import type {
  CareerGoal,
  CareerPlan,
  Activity,
  CareerMovesResponse,
  CareerMove,
  CareerMoveActionResult,
  CareerMoveActionRecord,
  CareerMoveActionKind,
  CareerMoveBucketFilter,
  CareerConfidenceLevel,
  EffortWeight,
} from "@career-ops-amplify/shared";

// ---- Goals ------------------------------------------------------------------

export async function listGoals(): Promise<{ goals: CareerGoal[] }> {
  return apiGet("/api/goals");
}

export async function getGoal(id: string): Promise<{ goal: CareerGoal }> {
  return apiGet(`/api/goals/${id}`);
}

export async function createGoal(input: {
  title: string;
  successDefinition: string;
  goalType?: string;
  targetDate?: string;
  status?: string;
  progressPercent?: number;
  externalUrl?: string;
  confidence?: CareerConfidenceLevel | string;
}): Promise<{ goal: CareerGoal }> {
  return apiPost("/api/goals", input);
}

export async function updateGoal(
  id: string,
  input: Partial<{
    title: string;
    successDefinition: string;
    goalType: string;
    targetDate: string | null;
    status: string;
    progressState: string;
    progressPercent: number;
    externalUrl: string | null;
    confidence: CareerConfidenceLevel | string | null;
    metrics: CareerGoal["metrics"];
  }>,
): Promise<{ goal: CareerGoal }> {
  return apiPatch(`/api/goals/${id}`, input);
}

export async function deleteGoal(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/api/goals/${id}`);
}

// ---- Plans ------------------------------------------------------------------

export async function listPlans(
  goalId?: string,
): Promise<{ plans: CareerPlan[] }> {
  const q = goalId ? `?goalId=${encodeURIComponent(goalId)}` : "";
  return apiGet(`/api/plans${q}`);
}

export async function getPlan(id: string): Promise<{ plan: CareerPlan }> {
  return apiGet(`/api/plans/${id}`);
}

export async function createPlan(input: {
  title: string;
  primaryObjective: string;
  goalId?: string;
  kind?: string;
  timeHorizon?: string;
  status?: string;
  rationale?: string;
  reviewCadence?: string;
}): Promise<{ plan: CareerPlan }> {
  return apiPost("/api/plans", input);
}

export async function updatePlan(
  id: string,
  input: Partial<{
    title: string;
    primaryObjective: string;
    goalId: string | null;
    kind: string;
    status: string;
    timeHorizon: string | null;
    rationale: string | null;
    reviewCadence: string | null;
    lastReviewedAt: string;
  }>,
): Promise<{ plan: CareerPlan }> {
  return apiPatch(`/api/plans/${id}`, input);
}

export async function deletePlan(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/api/plans/${id}`);
}

// ---- Activities (Work) ------------------------------------------------------

export async function listActivities(
  openOnly = false,
): Promise<{ activities: Activity[] }> {
  const q = openOnly ? "?open=1" : "";
  return apiGet(`/api/activities${q}`);
}

export async function getActivity(id: string): Promise<{ activity: Activity }> {
  return apiGet(`/api/activities/${id}`);
}

export async function createActivity(input: {
  title: string;
  type?: string;
  goalId?: string;
  planId?: string;
  jobId?: string;
  companySlug?: string;
  contactId?: string;
  description?: string;
  state?: string;
  dueAt?: string;
  channel?: string;
  priority?: string;
  progressPercent?: number;
  effortWeight?: EffortWeight | string;
  externalUrl?: string;
  confidence?: CareerConfidenceLevel | string;
}): Promise<{ activity: Activity }> {
  return apiPost("/api/activities", input);
}

export async function updateActivity(
  id: string,
  input: Partial<{
    title: string;
    type: string;
    goalId: string | null;
    planId: string | null;
    jobId: string | null;
    companySlug: string | null;
    contactId: string | null;
    description: string | null;
    state: string;
    dueAt: string | null;
    channel: string | null;
    priority: string | null;
    outcome: string | null;
    progressPercent: number;
    effortWeight: EffortWeight | string;
    externalUrl: string | null;
    confidence: CareerConfidenceLevel | string | null;
  }>,
): Promise<{ activity: Activity }> {
  return apiPatch(`/api/activities/${id}`, input);
}

export async function completeActivity(
  id: string,
  outcome?: string,
): Promise<{ activity: Activity }> {
  return apiPost(`/api/activities/${id}/complete`, { outcome });
}

export async function deleteActivity(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/api/activities/${id}`);
}

// ---- Career Moves -----------------------------------------------------------

export type ListCareerMovesParams = {
  bucket?: CareerMoveBucketFilter | string;
  limit?: number;
  cursor?: string;
  refresh?: boolean;
};

export async function listCareerMoves(
  params?: ListCareerMovesParams,
): Promise<CareerMovesResponse> {
  const sp = new URLSearchParams();
  if (params?.bucket && params.bucket !== "ALL_ACTIVE") {
    sp.set("bucket", params.bucket);
  }
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.cursor) sp.set("cursor", params.cursor);
  if (params?.refresh) sp.set("refresh", "1");
  const q = sp.toString();
  return apiGet(`/api/career-moves${q ? `?${q}` : ""}`);
}

export async function getCareerMove(
  id: string,
): Promise<{ move: CareerMove }> {
  return apiGet(`/api/career-moves/${id}`);
}

export async function executeCareerMoveAction(
  id: string,
  body: {
    action: CareerMoveActionKind | string;
    fingerprint?: string;
    materialStateHash?: string;
    generationId?: string;
    reason?: string;
    payload?: Record<string, unknown>;
  },
): Promise<CareerMoveActionResult> {
  return apiPost(`/api/career-moves/${id}/actions`, body);
}

export async function listCareerMoveActions(
  id: string,
): Promise<{ actions: CareerMoveActionRecord[] }> {
  return apiGet(`/api/career-moves/${id}/actions`);
}
